/*
 * FLOWER REWARD GAME JAVASCRIPT
 * Interactive flower picking experience with celebrations
 */

class FlowerRewardGame {
  constructor() {
    this.hasPlayed = false;
    this.customerEmail = null;
    this.selectedFlower = null;
    this.audioEnabled = true;
    this.ambientAudio = null;
    
    this.init();
    this.trackPageView();
  }

  init() {
    this.bindEvents();
    this.setupAudio();
    this.checkExistingPlayer();
  }

  bindEvents() {
    // Email form submission
    const emailForm = document.getElementById('email-form');
    if (emailForm) {
      emailForm.addEventListener('submit', (e) => this.handleEmailSubmission(e));
    }

    // Flower click events
    const flowers = document.querySelectorAll('.flower');
    flowers.forEach((flower, index) => {
      flower.addEventListener('click', () => this.pickFlower(flower, index + 1));
    });

    // Audio toggle
    const audioToggle = document.getElementById('audio-toggle');
    if (audioToggle) {
      audioToggle.addEventListener('click', () => this.toggleAudio());
    }

    // Copy code button
    const copyButton = document.getElementById('copy-code');
    if (copyButton) {
      copyButton.addEventListener('click', () => this.copyDiscountCode());
    }

    // Claim button
    const claimButton = document.getElementById('claim-button');
    if (claimButton) {
      claimButton.addEventListener('click', () => {
        const discountCode = document.getElementById('discount-code').textContent;
        this.trackClaimButtonClick(discountCode);
      });
    }

    // Keyboard accessibility
    document.addEventListener('keydown', (e) => this.handleKeyboardNav(e));
  }

  setupAudio() {
    this.ambientAudio = document.getElementById('ambient-nature');
    if (this.ambientAudio) {
      this.ambientAudio.volume = 0.3;
    }
  }

  checkExistingPlayer() {
    const playedEmails = JSON.parse(localStorage.getItem('flowerGamePlayed') || '[]');
    // For now, just show email gate - we'll check after email submission
  }

  async handleEmailSubmission(e) {
    e.preventDefault();
    
    const emailInput = e.target.querySelector('input[type="email"]');
    const submitButton = e.target.querySelector('button[type="submit"]');
    const email = emailInput.value.trim();

    if (!this.validateEmail(email)) {
      this.showEmailError('Please enter a valid email address');
      this.trackEmailSubmission(email, false);
      return;
    }

    // Check if email has already played
    const playedEmails = JSON.parse(localStorage.getItem('flowerGamePlayed') || '[]');
    if (playedEmails.includes(email)) {
      this.showEmailError('This email has already been used. One magical moment per email address.');
      this.trackEmailDuplicate();
      return;
    }

    submitButton.classList.add('loading');
    submitButton.disabled = true;

    try {
      // Simulate email submission (in real implementation, send to your backend)
      await this.simulateEmailSubmission(email);
      
      this.customerEmail = email;
      this.showGarden();
      this.startAmbientAudio();
      this.trackEmailSubmission(email, true);
      
    } catch (error) {
      this.showEmailError('Something went wrong. Please try again.');
      this.trackEmailSubmission(email, false);
    } finally {
      submitButton.classList.remove('loading');
      submitButton.disabled = false;
    }
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showEmailError(message) {
    // Remove any existing error
    const existingError = document.querySelector('.email-error');
    if (existingError) {
      existingError.remove();
    }

    // Add new error message
    const emailForm = document.getElementById('email-form');
    const errorDiv = document.createElement('div');
    errorDiv.className = 'email-error';
    errorDiv.style.cssText = `
      color: #d32f2f;
      font-size: 0.9rem;
      margin-top: 0.5rem;
      animation: shake 0.5s ease-in-out;
    `;
    errorDiv.textContent = message;
    emailForm.appendChild(errorDiv);

    // Add shake animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
      }
    `;
    document.head.appendChild(style);

    // Remove error after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  async simulateEmailSubmission(email) {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // In real implementation, you would:
    // 1. Send email to your backend
    // 2. Store email in database
    // 3. Send welcome email
    console.log(`Email submitted: ${email}`);
  }

  showGarden() {
    const emailGate = document.getElementById('email-gate');
    const garden = document.getElementById('flower-garden');
    
    emailGate.style.animation = 'fadeOut 0.8s ease-out forwards';
    
    setTimeout(() => {
      emailGate.style.display = 'none';
      garden.style.display = 'flex';
      garden.style.animation = 'fadeIn 1s ease-out';
    }, 800);
  }

  startAmbientAudio() {
    if (this.ambientAudio && this.audioEnabled) {
      this.ambientAudio.play().catch(e => {
        console.log('Ambient audio autoplay prevented:', e);
      });
    }
  }

  toggleAudio() {
    this.audioEnabled = !this.audioEnabled;
    const audioToggle = document.getElementById('audio-toggle');
    const audioOnIcon = audioToggle.querySelector('.audio-on');
    const audioOffIcon = audioToggle.querySelector('.audio-off');

    if (this.audioEnabled) {
      audioOnIcon.style.display = 'block';
      audioOffIcon.style.display = 'none';
      if (this.ambientAudio) {
        this.ambientAudio.play();
      }
    } else {
      audioOnIcon.style.display = 'none';
      audioOffIcon.style.display = 'block';
      if (this.ambientAudio) {
        this.ambientAudio.pause();
      }
    }

    // Track audio toggle
    this.trackAudioToggle(this.audioEnabled);
  }

  async pickFlower(flowerElement, flowerNumber) {
    if (this.hasPlayed) return;

    this.hasPlayed = true;
    this.selectedFlower = flowerNumber;

    // Add picked class and animate
    flowerElement.classList.add('picked');
    
    // Disable all other flowers
    const allFlowers = document.querySelectorAll('.flower');
    allFlowers.forEach(flower => {
      if (flower !== flowerElement) {
        flower.style.pointerEvents = 'none';
        flower.style.opacity = '0.3';
      }
    });

    // Get reward code from the flower
    const rewardCode = flowerElement.dataset.reward || 'FLOWER15';

    // Track flower selection
    this.trackFlowerSelected(flowerNumber, rewardCode);

    // Animate flower upward
    flowerElement.style.animation = 'flowerPicked 0.8s ease-out, floatUp 2s ease-out 0.8s forwards';

    // Add floating animation
    this.addFloatAnimation();

    // Wait for flower animation, then show celebration
    setTimeout(() => {
      this.showCelebration(rewardCode);
      this.createConfetti();
      this.savePlayerEmail();
      this.trackDiscountCodeRevealed(rewardCode);
    }, 2800);
  }

  addFloatAnimation() {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes floatUp {
        0% { transform: scale(1.05) translateY(0); opacity: 1; }
        100% { transform: scale(0.8) translateY(-50px); opacity: 0.7; }
      }
      @keyframes fadeOut {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  showCelebration(discountCode) {
    const celebrationModal = document.getElementById('celebration-modal');
    const discountCodeElement = document.getElementById('discount-code');
    
    discountCodeElement.textContent = discountCode;
    
    celebrationModal.style.display = 'flex';
    
    // Play celebration sound effect if audio enabled
    if (this.audioEnabled) {
      this.playSuccessSound();
    }
  }

  createConfetti() {
    const confettiContainer = document.getElementById('confetti-container');
    const colors = ['#f3bf9f', '#a3be8c', '#8B7355', '#f2d1ba', '#c9b9a5'];
    
    // Create 50 confetti pieces
    for (let i = 0; i < 50; i++) {
      setTimeout(() => {
        this.createConfettiPiece(confettiContainer, colors);
      }, i * 100);
    }
  }

  createConfettiPiece(container, colors) {
    const confetti = document.createElement('div');
    confetti.className = 'confetti-piece';
    
    // Random properties
    const color = colors[Math.floor(Math.random() * colors.length)];
    const size = Math.random() * 8 + 4;
    const left = Math.random() * 100;
    const animationDuration = Math.random() * 2 + 2;
    const animationDelay = Math.random() * 0.5;
    
    confetti.style.cssText = `
      background: ${color};
      width: ${size}px;
      height: ${size}px;
      left: ${left}%;
      animation-duration: ${animationDuration}s;
      animation-delay: ${animationDelay}s;
    `;
    
    container.appendChild(confetti);
    
    // Remove confetti piece after animation
    setTimeout(() => {
      confetti.remove();
    }, (animationDuration + animationDelay) * 1000);
  }

  playSuccessSound() {
    // Create a simple success sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Web Audio API not supported');
    }
  }

  copyDiscountCode() {
    const discountCode = document.getElementById('discount-code').textContent;
    const copyButton = document.getElementById('copy-code');
    
    navigator.clipboard.writeText(discountCode).then(() => {
      const originalText = copyButton.textContent;
      copyButton.textContent = 'Copied!';
      copyButton.style.background = '#a3be8c';
      
      // Track code copied
      this.trackCodeCopied(discountCode);
      
      setTimeout(() => {
        copyButton.textContent = originalText;
        copyButton.style.background = '#8B7355';
      }, 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = discountCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Track code copied
      this.trackCodeCopied(discountCode);
      
      const originalText = copyButton.textContent;
      copyButton.textContent = 'Copied!';
      setTimeout(() => {
        copyButton.textContent = originalText;
      }, 2000);
    });
  }

  savePlayerEmail() {
    if (this.customerEmail) {
      const playedEmails = JSON.parse(localStorage.getItem('flowerGamePlayed') || '[]');
      playedEmails.push(this.customerEmail);
      localStorage.setItem('flowerGamePlayed', JSON.stringify(playedEmails));
    }
  }

  handleKeyboardNav(e) {
    // ESC to close modals
    if (e.key === 'Escape') {
      const emailGate = document.getElementById('email-gate');
      const celebrationModal = document.getElementById('celebration-modal');
      
      if (emailGate.style.display !== 'none') {
        // Don't allow closing email gate
        return;
      }
      
      if (celebrationModal.style.display === 'flex') {
        celebrationModal.style.display = 'none';
      }
    }
    
    // Space or Enter to toggle audio
    if (e.key === ' ' || e.key === 'Enter') {
      if (e.target.id === 'audio-toggle') {
        e.preventDefault();
        this.toggleAudio();
      }
    }
  }

  // ===== ANALYTICS TRACKING =====
  
  trackPageView() {
    this.trackEvent('flower_game', 'page_view', {
      page_title: 'Flower Reward Game',
      page_location: window.location.href
    });
  }

  trackEvent(category, action, data = {}) {
    const eventData = {
      event_category: category,
      event_action: action,
      ...data
    };

    // Google Analytics 4 (gtag)
    if (typeof gtag !== 'undefined') {
      gtag('event', action, {
        event_category: category,
        ...data
      });
    }

    // Universal Analytics (ga)
    if (typeof ga !== 'undefined') {
      ga('send', 'event', category, action, data.label || '', data.value || 0);
    }

    // Shopify Analytics (Shopify.analytics)
    if (typeof Shopify !== 'undefined' && Shopify.analytics) {
      Shopify.analytics.publish('flower_game_event', {
        category: category,
        action: action,
        ...data
      });
    }

    // DataLayer (Google Tag Manager)
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({
        event: 'flower_game_event',
        event_category: category,
        event_action: action,
        ...data
      });
    }

    // Custom event dispatch for other analytics
    window.dispatchEvent(new CustomEvent('flowerGame:track', {
      detail: eventData
    }));

    // Console log for debugging (remove in production if desired)
    console.log('Flower Game Analytics:', eventData);
  }

  trackEmailSubmission(email, success) {
    if (success) {
      this.trackEvent('flower_game', 'email_submitted', {
        email_domain: email.split('@')[1] || 'unknown',
        label: 'Email Gate Passed'
      });
    } else {
      this.trackEvent('flower_game', 'email_error', {
        label: 'Email Validation Failed'
      });
    }
  }

  trackEmailDuplicate() {
    this.trackEvent('flower_game', 'email_duplicate', {
      label: 'Duplicate Email Attempt'
    });
  }

  trackFlowerSelected(flowerNumber, rewardCode) {
    this.trackEvent('flower_game', 'flower_selected', {
      flower_number: flowerNumber,
      discount_code: rewardCode,
      label: `Flower ${flowerNumber} - ${rewardCode}`
    });
  }

  trackDiscountCodeRevealed(rewardCode) {
    this.trackEvent('flower_game', 'discount_revealed', {
      discount_code: rewardCode,
      label: `Code Revealed: ${rewardCode}`
    });
  }

  trackCodeCopied(rewardCode) {
    this.trackEvent('flower_game', 'code_copied', {
      discount_code: rewardCode,
      label: `Code Copied: ${rewardCode}`
    });
  }

  trackClaimButtonClick(rewardCode) {
    this.trackEvent('flower_game', 'claim_button_clicked', {
      discount_code: rewardCode,
      label: `Claim Button: ${rewardCode}`,
      value: 1
    });
  }

  trackAudioToggle(enabled) {
    this.trackEvent('flower_game', 'audio_toggled', {
      audio_state: enabled ? 'on' : 'off',
      label: `Audio ${enabled ? 'Enabled' : 'Disabled'}`
    });
  }
}

// Initialize the game when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new FlowerRewardGame();
});

// Export for potential external use
window.FlowerRewardGame = FlowerRewardGame; 